import { Component } from '@opten/gin-tonic/helpers/compiler';

// Mobile tap-to-toggle for tiles: mirrors :hover state with an `is-active` class

@Component({ className: 'js-tiles' })
export class TilesTouchComponent {
	private static readonly TAP_MOVE_THRESHOLD = 10; // px

	// Per-card gesture state and listener references for cleanup
	private states = new Map<
		HTMLElement,
		{ startX: number; startY: number; moved: boolean }
	>();
	private listeners = new Map<
		HTMLElement,
		{
			start: (e: TouchEvent) => void;
			move: (e: TouchEvent) => void;
			end: (e: TouchEvent) => void;
			cancel: (e: TouchEvent) => void;
		}
	>();

	constructor(private element: HTMLElement) {
		// Only enable on devices where hover isn't primary (touch-first)
		if (this.canHover()) return;
		this.setup();
	}

	private canHover(): boolean {
		return window.matchMedia('(any-hover: hover)').matches;
	}

	private setup() {
		const cards = this.element.querySelectorAll<HTMLElement>('.c-tile-card');
		cards.forEach((card) => this.attach(card));

		// On touch-first devices (we only call setup when hover isn't primary),
		// make the first tile active on load unless one is already active.
		const hasActive = this.element.querySelector('.c-tile-card.is-active');
		if (!hasActive && cards.length > 0) {
			const first = cards[0];
			first.classList.add('is-active');
		}
	}

	private attach(card: HTMLElement) {
		const start = (e: TouchEvent) => {
			if (e.touches.length > 0) {
				const t = e.touches[0];
				this.states.set(card, {
					startX: t.clientX,
					startY: t.clientY,
					moved: false,
				});
			}
		};

		const move = (e: TouchEvent) => {
			const st = this.states.get(card);
			if (!st || st.moved) return;
			if (e.touches.length > 0) {
				const t = e.touches[0];
				const dx = Math.abs(t.clientX - st.startX);
				const dy = Math.abs(t.clientY - st.startY);
				if (
					dx > TilesTouchComponent.TAP_MOVE_THRESHOLD ||
					dy > TilesTouchComponent.TAP_MOVE_THRESHOLD
				) {
					st.moved = true;
				}
			}
		};

		const end = (_e: TouchEvent) => {
			const st = this.states.get(card);
			this.states.delete(card);
			if (!st || st.moved) return; // treat as swipe/scroll
			// Toggle active; ensure exclusivity within this tiles container
			const wasActive = card.classList.contains('is-active');
			this.element
				.querySelectorAll('.c-tile-card.is-active')
				.forEach((el) => el.classList.remove('is-active'));
			if (!wasActive) card.classList.add('is-active');
		};

		const cancel = (_e: TouchEvent) => {
			const st = this.states.get(card);
			if (st) st.moved = true; // cancel tap
		};

		this.listeners.set(card, { start, move, end, cancel });
		card.addEventListener('touchstart', start, { passive: true });
		card.addEventListener('touchmove', move, { passive: true });
		card.addEventListener('touchend', end);
		card.addEventListener('touchcancel', cancel);
	}

	public destroy() {
		// Remove all listeners we attached
		this.listeners.forEach((handlers, card) => {
			card.removeEventListener('touchstart', handlers.start as EventListener);
			card.removeEventListener('touchmove', handlers.move as EventListener);
			card.removeEventListener('touchend', handlers.end as EventListener);
			card.removeEventListener('touchcancel', handlers.cancel as EventListener);
		});
		this.listeners.clear();
		this.states.clear();
	}
}
